// frontend/src/Components/Tasks/TasksSection.jsx
//
// Dashboard Tasks section. Slot at the top of the middle column above
// Patient Search.
//
// Filters (pills): Mine (default) / Support / All / Done.
//   - Mine     → tasks assigned to me OR @mentioning me (open).
//   - Support  → SUPPORT-type tasks I created (JIRA-backed support tickets).
//   - All      → tasks I'm involved with: assigned to me ∪ mentioned ∪
//                created by me (open). Same scope for every role —
//                SuperAdmin does not see other users' tasks here.
//   - Done     → DONE tasks within the same personal scope as 'All'.
//
// Pagination: PAGE_SIZE rows per filter (server-paginated). Page resets to 1
// when the user switches view.
//
// Mount semantics: the section stays mounted whenever the user has ANY
// tasks in their visibility scope (open or done) — the server-returned
// `hasAny` flag drives this. So a user with only done tasks still sees the
// pills + the section header and can click Done to view them. The section
// only unmounts when there are zero tasks of any kind.
//
// Polling: 30s — light enough not to hammer the API, frequent enough to show
// new mentions / activity bumps without a manual refresh.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Card, Spinner, Stack } from 'react-bootstrap';
import { jwtDecode } from 'jwt-decode';
import { listMyTasks } from '../../api';
import TaskListItem from './TaskListItem';
import TaskDetailModal from './TaskDetailModal';

const POLL_MS = 30000;
const PAGE_SIZE = 10;

const VIEWS = [
  { key: 'mine',    label: 'Mine' },
  { key: 'support', label: 'Support' },
  { key: 'all',     label: 'All' },
  { key: 'done',    label: 'Done' },
];

function readUserIdFromJwt() {
  try {
    const token = sessionStorage.getItem('user');
    if (!token) return null;
    const decoded = jwtDecode(token);
    return decoded?._id || decoded?.id || null;
  } catch {
    return null;
  }
}

export default function TasksSection({ openTaskId, onOpenTaskHandled }) {
  const [tasks, setTasks] = useState([]);
  const [hasAny, setHasAny] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [viewCounts, setViewCounts] = useState({ mine: 0, support: 0, all: 0, done: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Always default to 'Mine' on mount. Previously persisted in localStorage,
  // but the dashboard should land on the user's personal tasks every session.
  const [view, setView] = useState('mine');
  const [page, setPage] = useState(1);
  const [activeTaskId, setActiveTaskId] = useState(null);
  const cancelledRef = useRef(false);
  const currentUserId = useMemo(() => readUserIdFromJwt(), []);

  // Ref to the "Done" pill button. TaskListItem reads this so the
  // mark-done checkbox can fly a confirmation pill into the Done filter,
  // simulating the task "landing" in the Done bucket.
  const doneButtonRef = useRef(null);
  const getDoneButtonEl = useCallback(() => doneButtonRef.current, []);

  const refresh = useCallback(async () => {
    const data = await listMyTasks({ view, page, limit: PAGE_SIZE });
    if (cancelledRef.current) return;
    setTasks(Array.isArray(data?.tasks) ? data.tasks : []);
    setHasAny(!!data?.hasAny);
    setTotalCount(typeof data?.totalCount === 'number' ? data.totalCount : 0);
    if (data?.viewCounts && typeof data.viewCounts === 'object') {
      setViewCounts({
        mine:    Number(data.viewCounts.mine)    || 0,
        support: Number(data.viewCounts.support) || 0,
        all:     Number(data.viewCounts.all)     || 0,
        done:    Number(data.viewCounts.done)    || 0,
      });
    }
    setError(null);
    setLoading(false);
  }, [view, page]);

  useEffect(() => {
    cancelledRef.current = false;
    setLoading(true);
    refresh().catch((e) => {
      if (cancelledRef.current) return;
      setError(e?.message || 'Could not load tasks');
      setLoading(false);
    });
    const id = setInterval(() => { refresh().catch(() => {}); }, POLL_MS);
    // Cross-component nudge: BootNav's CreateTaskModal dispatches
    // `tasks:created` after a successful POST so the dashboard updates
    // without waiting for the 30s poll.
    const onTaskCreated = () => { refresh().catch(() => {}); };
    window.addEventListener('tasks:created', onTaskCreated);
    return () => {
      cancelledRef.current = true;
      clearInterval(id);
      window.removeEventListener('tasks:created', onTaskCreated);
    };
  }, [refresh]);

  // Open a task when ?taskId=… deep link arrives (from notification click).
  useEffect(() => {
    if (openTaskId) {
      setActiveTaskId(String(openTaskId));
      onOpenTaskHandled?.();
    }
  }, [openTaskId, onOpenTaskHandled]);

  const onSelectView = (next) => {
    if (!VIEWS.some((v) => v.key === next)) return;
    setView(next);
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const canPrev = page > 1;
  const canNext = page < totalPages;

  // Always render the section — even when the user has zero tasks across
  // every filter. The per-view empty state below covers the "nothing here"
  // case so users always see the Tasks card on their dashboard.
  const emptyForFilter = !loading && !error && tasks.length === 0;

  return (
    <>
      <Card className="section-card mb-3 border-0 rounded-0">
        <Card.Header className="bg-white border-0 section-card__header">
          <Stack direction="horizontal" gap={2} className="align-items-center w-100">
            <div className="pe-2 d-flex align-items-center gap-2">
              <h6 className="text-primary mb-0 fw-semibold">Tasks</h6>
            </div>
          </Stack>
          <div className="mt-3 d-flex gap-1">
            {VIEWS.map((v) => {
              const isActive = view === v.key;
              const unread = viewCounts[v.key] || 0;
              return (
                <Button
                  key={v.key}
                  ref={v.key === 'done' ? doneButtonRef : null}
                  size="sm"
                  onClick={() => onSelectView(v.key)}
                  className="rounded-pill flex-fill text-center d-inline-flex align-items-center justify-content-center gap-1"
                  style={{
                    fontSize: '0.75rem',
                    padding: '0.4rem 0.9rem',
                    lineHeight: '1.5',
                    // flex-basis:0 makes every button take an equal slice of
                    // the row regardless of label length (ALL / MINE / MENTIONS
                    // / DONE differ widely in width otherwise).
                    flexBasis: 0,
                    // Match ListView's `.bgheader` light-blue treatment.
                    backgroundColor: isActive ? 'rgb(206, 226, 254)' : 'transparent',
                    color: '#0f3490',
                    borderColor: 'rgb(206, 226, 254)',
                  }}
                >
                  <span>{v.label}</span>
                  {unread > 0 && (
                    <Badge bg="primary" pill style={{ fontSize: '0.65rem' }}>
                      {unread}
                    </Badge>
                  )}
                </Button>
              );
            })}
          </div>
        </Card.Header>
        <Card.Body className="section-card__body p-0">
          {loading && (
            <div className="text-center text-muted py-3">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading your tasks…
            </div>
          )}
          {!loading && error && (
            <div className="text-center text-danger small py-3">
              We could not load your tasks. {' '}
              <Button variant="link" size="sm" onClick={() => refresh().catch(() => {})}>
                Retry
              </Button>
            </div>
          )}
          {emptyForFilter && (
            <div className="text-center text-muted small py-3">
              {view === 'done'
                ? 'No completed tasks yet.'
                : view === 'support'
                ? 'No open support tickets.'
                : view === 'mine'
                ? 'No tasks assigned to you right now.'
                : 'No open tasks involving you right now.'}
            </div>
          )}
          {!loading && !error && tasks.map((task) => (
            <TaskListItem
              key={task._id}
              task={task}
              currentUserId={currentUserId}
              onOpen={() => setActiveTaskId(task._id)}
              onChanged={() => refresh().catch(() => {})}
              getDoneButtonEl={getDoneButtonEl}
            />
          ))}
          {!loading && !error && totalCount > PAGE_SIZE && (
            <div className="d-flex align-items-center justify-content-between px-3 py-2 border-top">
              <small className="text-muted">
                Page {page} of {totalPages} · {totalCount} total
              </small>
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={!canPrev}
                  onClick={() => canPrev && setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={!canNext}
                  onClick={() => canNext && setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
      <TaskDetailModal
        taskId={activeTaskId}
        show={!!activeTaskId}
        onClose={() => { setActiveTaskId(null); refresh().catch(() => {}); }}
        currentUserId={currentUserId}
      />
    </>
  );
}
